import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowUpRight, ArrowRight, Hammer, Compass, Sparkles, Star } from "lucide-react";

const HERO = "https://images.pexels.com/photos/33685860/pexels-photo-33685860.jpeg";
const KITCHEN = "https://images.pexels.com/photos/19836790/pexels-photo-19836790.jpeg";
const BATH = "https://images.pexels.com/photos/33599113/pexels-photo-33599113.jpeg";
const TEAM = "https://images.pexels.com/photos/13375852/pexels-photo-13375852.jpeg";

const MARQUEE = ["Italian Marble", "Hettich Hardware", "PU Finishes", "Quartz Tops", "Veneer Joinery", "Brushed Brass", "Linen Drapery", "Walnut Detail", "Travertine", "Smart Lighting"];

export default function Home() {
  const [portfolio, setPortfolio] = useState([]);
  const [testi, setTesti] = useState([]);

  useEffect(() => {
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 4))).catch(() => {});
    api.get("/testimonials").then((r) => setTesti(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative -mt-20">
        <div className="relative h-[94vh] min-h-[680px] overflow-hidden">
          <div className="absolute inset-0 animate-kenburns">
            <img src={HERO} alt="Luxury living interior" className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-black/75" />
          <div className="absolute inset-0 px-6 md:px-12 lg:px-24 flex flex-col justify-between pt-28 pb-10">
            <div className="flex flex-wrap justify-between items-start gap-x-6 gap-y-2 text-white/90 animate-fadeIn">
              <p className="overline text-[#E8D9B3]" data-testid="hero-overline">Bengaluru · Est. 2022</p>
              <p className="hidden md:block text-xs tracking-[0.3em] uppercase text-white/70" data-testid="hero-warranty">5-Year Workmanship Warranty · Hardware &amp; materials per manufacturer</p>
            </div>

            <div className="max-w-4xl text-white">
              <h1 className="font-heading text-5xl sm:text-7xl lg:text-[8rem] font-light tracking-[-0.02em] leading-[0.95] animate-fadeUp">
                Homes designed<br />
                with <em className="italic font-light text-[#E8D9B3]">spatial silence.</em>
              </h1>
              <p className="mt-8 text-base sm:text-lg text-white/85 max-w-xl leading-[1.8] animate-fadeUp delay-300">
                For homeowners investing ₹10L to ₹50L — Dreamy Interiors crafts considered, cinematic homes from drawing to handover.
              </p>
              <div className="mt-12 flex flex-wrap gap-4 animate-fadeUp delay-500">
                <Link data-testid="hero-cta-book" to="/book" className="btn-gold">
                  Book a Consultation <ArrowRight size={14} />
                </Link>
                <Link data-testid="hero-cta-estimate" to="/estimator" className="btn-ghost-light">
                  Estimate My Interiors
                </Link>
              </div>
            </div>

            <div className="hidden md:flex items-end justify-between text-white/70 text-xs uppercase tracking-[0.3em] animate-fadeIn delay-700">
              <span>Scroll to explore</span>
              <div className="flex items-center gap-2">
                <div className="h-[1px] w-12 bg-white/50" />
                <span>01 / 04</span>
              </div>
            </div>
          </div>
        </div>

        {/* MARQUEE */}
        <div className="bg-[#1C1A17] text-[#E8D9B3] py-5 overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <span key={i} className="mx-8 font-heading italic text-2xl tracking-wide flex items-center gap-8">
                {m} <span className="text-[#C9A24A]">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="px-6 md:px-12 lg:px-24 py-28 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <p className="overline">Our Approach</p>
          <h2 className="font-heading text-4xl sm:text-6xl font-light tracking-[-0.01em] mt-6 leading-[1.05]">
            Not just designed — <em className="italic">considered.</em>
          </h2>
          <div className="gold-rule mt-10 w-32" />
        </div>
        <div className="lg:col-span-7 lg:pt-3">
          <p className="text-lg leading-[1.95] text-[#1C1A17]/80 max-w-2xl font-light">
            We treat your home like a private commission. Every joinery line, lighting layer, and material choice is debated in-house, drawn on paper, and rehearsed in 3D before a single carpenter lifts a tool. The result is an interior that ages slowly and beautifully — never trend-tied, always yours.
          </p>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: Compass, k: "Design First", v: "Concept, 3D & joinery details before quotation." },
              { icon: Hammer,  k: "Turnkey Build", v: "In-house carpenters, civil, electricals & polish." },
              { icon: Sparkles, k: "Warranty You Can Trust", v: "5-year workmanship warranty. Hardware and materials as per manufacturer warranty." },
            ].map(({ icon: Icon, k, v }, i) => (
              <div key={k} className={`card-soft p-7 animate-fadeUp delay-${i*150 || 150}`}>
                <div className="h-12 w-12 rounded-full bg-[#F5F0E6] grid place-items-center">
                  <Icon className="text-[#A07F2D]" size={20} strokeWidth={1.4} />
                </div>
                <p className="mt-5 font-heading text-2xl">{k}</p>
                <p className="mt-2 text-sm text-[#5C5752] leading-relaxed">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW */}
      <section className="wood-texture grain-overlay">
        <div className="px-6 md:px-12 lg:px-24 py-28 md:py-40">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <p className="overline">Recent Work</p>
              <h2 className="font-heading text-4xl sm:text-6xl font-light tracking-[-0.01em] mt-4 leading-[1.05]">Selected projects</h2>
            </div>
            <Link to="/portfolio" data-testid="home-view-all-portfolio" className="btn-secondary">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14">
            {portfolio.map((p, i) => (
              <Link key={p.id} to="/portfolio" data-testid={`home-portfolio-${i}`} className={`group block ${i % 2 ? "md:translate-y-20" : ""}`}>
                <div className="img-zoom rounded-img-lg">
                  <img src={p.cover_image} alt={p.title} className="w-full aspect-[4/5] object-cover" />
                </div>
                <div className="mt-6 flex items-baseline justify-between px-2">
                  <div>
                    <p className="font-heading text-3xl">{p.title}</p>
                    <p className="text-sm text-[#5C5752] mt-1.5 tracking-wide">{p.location} · {p.bhk}</p>
                  </div>
                  <span className="text-xs uppercase tracking-[0.25em] text-[#A07F2D]">{p.style}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT CTA – Estimator */}
      <section className="px-6 md:px-12 lg:px-24 py-28 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <p className="overline">Cost Estimator</p>
            <h2 className="font-heading text-4xl sm:text-6xl font-light tracking-[-0.01em] mt-4 leading-[1.05]">
              A clearer starting point <em className="italic">for your interiors.</em>
            </h2>
            <p className="mt-7 max-w-md text-[#5C5752] leading-[1.9] text-lg font-light">
              Tell us about your home, requirements and preferred finish. Get a transparent preliminary estimate to help you plan your interiors with confidence.
            </p>
            <Link to="/estimator" data-testid="home-estimator-cta" className="btn-primary mt-12">
              Try the Estimator <ArrowRight size={14} />
            </Link>
          </div>
          <div className="img-zoom rounded-img-lg">
            <img src={KITCHEN} alt="Modern modular kitchen" className="w-full aspect-[4/3] object-cover" />
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#1C1A17] text-white py-28 md:py-36 overflow-hidden">
        <div className="px-6 md:px-12 lg:px-24">
          <p className="overline text-[#E8D9B3]">Our Process</p>
          <h2 className="font-heading text-4xl sm:text-6xl font-light tracking-[-0.01em] mt-4 max-w-3xl leading-[1.05]">
            Six chapters from <em className="italic text-[#E8D9B3]">first call to handover.</em>
          </h2>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-10">
            {[
              ["01", "Consultation", "We meet, we listen, we walk your floor plan."],
              ["02", "Design Concept", "Moodboards, 3D renders, material palette."],
              ["03", "Quotation", "Transparent, room-by-room, no hidden line items."],
              ["04", "Joinery & Civil", "In-house team, weekly site visits, daily photos."],
              ["05", "Finishing", "Polish, painting, lighting calibration."],
              ["06", "Handover", "Keys, manuals, and a 5-year workmanship promise (hardware/materials per manufacturer warranty)."],
            ].map(([n, t, d]) => (
              <div key={n} className="border-t border-white/15 pt-6">
                <p className="font-heading text-5xl text-[#C9A24A]">{n}</p>
                <p className="mt-4 font-heading text-2xl">{t}</p>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — hidden entirely until verified real client stories are ready */}
      {testi.length > 0 && (
        <section data-testid="testimonials-section" className="px-6 md:px-12 lg:px-24 py-28 md:py-36">
          <div className="text-center max-w-3xl mx-auto">
            <p className="overline">Owner Stories</p>
            <h2 className="font-heading text-4xl sm:text-6xl font-light tracking-[-0.01em] mt-4 leading-[1.05]">
              Quietly obsessed clients.
            </h2>
          </div>
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {testi.map((t, i) => (
              <figure key={i} data-testid={`testimonial-${i}`} className="card-soft p-9 flex flex-col">
                <div className="flex gap-1 text-[#C9A24A]">
                  {[...Array(t.rating || 5)].map((_, k) => <Star key={k} size={14} fill="currentColor" stroke="none" />)}
                </div>
                <blockquote className="mt-6 font-heading text-2xl leading-snug text-[#1C1A17] italic">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-auto pt-8 text-sm">
                  <span className="font-medium block">{t.name}</span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-[#A07F2D] mt-1.5">{t.location}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
        <div className="absolute inset-0 animate-kenburns">
          <img src={BATH} alt="Marble bathroom suite" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/40" />
        <div className="relative h-full flex flex-col items-center justify-center text-white text-center px-6">
          <p className="overline text-[#E8D9B3]">Begin your project</p>
          <h2 className="font-heading text-5xl sm:text-7xl font-light tracking-[-0.01em] mt-6 max-w-4xl leading-[1.02]">
            A 45-minute consultation.<br /><em className="italic text-[#E8D9B3]">No obligations.</em>
          </h2>
          <Link to="/book" data-testid="final-cta-book" className="btn-gold mt-12">
            Book Consultation <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
